import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../../config";

export default function SendNotificationModal({ isOpen, onClose, targetUser }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.includes("fa");

  const admin = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const token = admin?.token;

  const [content, setContent] = useState("");
  const [type, setType] = useState("admin_message");
  const [channel, setChannel] = useState("in_app");
  const [sending, setSending] = useState(false);

  if (!isOpen) return null;

  const submit = async () => {
    if (!content.trim()) return toast.error("متن پیام را وارد کنید");
    if (!targetUser?._id) return toast.error("کاربر نامعتبر");

    setSending(true);
    try {
      const res = await fetch(`${BASE_URL}/notification`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
        body: JSON.stringify({
          user: targetUser._id,
          content: content.trim(),
          type,
          channel,
        }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "FAILED");

      toast.success(t("notification.sent_success", "نوتیفیکیشن ارسال شد ✅"));
      setContent("");
      onClose?.();
    } catch (e) {
      toast.error(e?.message || "خطا در ارسال نوتیفیکیشن");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <div className="text-sm font-bold text-gray-800">
            {t("notification.send_to", "ارسال نوتیف")} :{" "}
            {targetUser?.name || targetUser?.email}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-200">
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4" dir={isRtl ? "rtl" : "ltr"}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("notification.content", "متن پیام")}
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              className="w-full border rounded-xl px-3 py-2 text-sm"
              placeholder={t("notification.placeholder", "پیام را وارد کنید...")}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t("notification.type", "نوع")}
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full border rounded-xl px-3 py-2 text-sm"
              >
                <option value="admin_message">پیام ادمین</option>
                <option value="exam_result">نتیجه آزمون</option>
                <option value="subscription">اشتراک</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {t("notification.channel", "کانال")}
              </label>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                className="w-full border rounded-xl px-3 py-2 text-sm"
              >
                <option value="in_app">داخل برنامه</option>
                <option value="email">ایمیل</option>
                <option value="sms">SMS</option>
                <option value="telegram">تلگرام</option>
                <option value="whatsapp">واتساپ</option>
              </select>
            </div>
          </div>

          <button
            onClick={submit}
            disabled={sending}
            className={`w-full py-2 rounded-xl text-white font-medium ${
              sending ? "bg-gray-400" : "bg-purple-600 hover:bg-purple-700"
            }`}
          >
            {sending ? "در حال ارسال..." : "ارسال نوتیفیکیشن"}
          </button>
        </div>
      </div>
    </div>
  );
}
